/**
 * /login — dedicated login page.
 *
 * Uses the existing AuthContext.login() flow so it stays in sync with the
 * AuthModal.
 */
import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function LoginPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [remember, setRemember] = useState(true);

  const dest = loc.state?.from || "/";

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Enter your email and password.");
      return;
    }
    setBusy(true);
    try {
      await login(email.trim(), password, remember);
      toast.success("Welcome back.");
      nav(dest, { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed. Check your email + password.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-neutral-200 flex items-center justify-center p-4">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm rounded-xl border border-amber-700/40 bg-neutral-950 p-6 shadow-2xl"
        data-testid="login-form"
      >
        <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 mb-1 text-center">
          🐦‍🔥 Omnimuscle
        </div>
        <h1 className="text-3xl font-black text-amber-200 mb-6 text-center" style={{ fontFamily: "Anton, sans-serif" }}>
          Sign In
        </h1>

        <label htmlFor="login-page-email" className="block text-xs uppercase tracking-wider text-neutral-400 mb-1">Email</label>
        <input
          id="login-page-email"
          name="login_page_email"
          type="email"
          autoComplete="username webauthn"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="login-email"
          className="w-full px-3 py-2 mb-4 rounded bg-black/60 border border-neutral-700 text-sm focus:border-amber-500 outline-none"
          required
        />

        <label htmlFor="login-page-password" className="block text-xs uppercase tracking-wider text-neutral-400 mb-1">Password</label>
        <input
          id="login-page-password"
          name="login_page_password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="login-password"
          className="w-full px-3 py-2 mb-3 rounded bg-black/60 border border-neutral-700 text-sm focus:border-amber-500 outline-none"
          required
        />

        <label className="flex items-center gap-2 text-xs text-neutral-400 mb-5 cursor-pointer">
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            data-testid="login-remember"
          />
          Remember me
        </label>

        <button
          type="submit"
          disabled={busy}
          data-testid="login-submit"
          className="w-full px-4 py-2.5 rounded-md bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-black text-sm transition"
        >
          {busy ? "Signing in…" : "Sign In"}
        </button>

        <div className="mt-4 text-center text-xs text-neutral-500">
          <Link to="/" className="text-amber-400 hover:text-amber-300" data-testid="login-back-home">← Back home</Link>
        </div>
      </form>
    </div>
  );
}
